<template>
  <div class="dropdown absolute rounded-sm bg-white shadow-sm">
    <div class="flex flex-col space-y-3 p-3">
      <search-input v-model="search" no-padding no-margin :delay="0" />
      <ul class="flex max-h-50 flex-col space-y-2 overflow-y-auto">
        <li class="list-item">
          <input
            id="attributes-select-all"
            v-model="areAllOptionsSelected"
            type="checkbox"
          />
          <label for="attributes-select-all" class="text-gray-400">
            {{ $t("COMMON.SELECT_ALL") }}
          </label>
        </li>
        <li
          v-for="(attr, attrIndex) in dropdownEntries"
          :key="`${attr}-${attrIndex}`"
          class="list-item"
          :data-cy="getAttributeId(attr)"
        >
          <input
            :id="`cb-${attr}-${attrIndex}`"
            v-model="selected"
            :value="getAttributeId(attr)"
            type="checkbox"
          />
          <label
            ref="attributeLabelRef"
            v-tooltip="{
              content: getAttributeName(attr),
              onShow: () => isOptionLabelTruncated[attrIndex],
            }"
            :for="`cb-${attr}-${attrIndex}`"
            class="max-w-64 truncate text-gray-600"
          >
            {{ getAttributeName(attr) }}
          </label>
        </li>
      </ul>
    </div>
    <hr />
    <div class="p-4">
      <primary-button
        data-cy="attribute-dropdown-save-button"
        class="w-full uppercase"
        @click="handleSave"
      >
        {{ $t("COMMON.SAVE") }}
      </primary-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import { getAttributeId } from "@/helpers/customAttributes";
import { wordsFirstLetterToUpper } from "@/helpers/formatting";
import { useElementStatus } from "@/hooks/elements";
import { useAttributesOptions } from "@/hooks/orchestration";

import PrimaryButton from "@/components/ui/buttons/PrimaryButton.vue";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";

import type { IStepAttribute } from "@/models/orchestration";
import type { PropType } from "vue";

const { attributeOptions } = useAttributesOptions();

const props = defineProps({
  attributes: {
    type: Array as PropType<IStepAttribute[]>,
    required: true,
  },
  selectedAttributes: {
    type: Array as PropType<IStepAttribute[]>,
    default: () => [],
  },
});

const emit = defineEmits<{
  (eventName: "save", options: IStepAttribute[]): void;
}>();

const selected = ref(props.selectedAttributes.map(getAttributeId));
const search = ref("");
const attributeLabelRef = ref<(HTMLElement | null)[]>([]);

const { isTextTruncated } = useElementStatus();

const isOptionLabelTruncated = isTextTruncated(attributeLabelRef);

const dropdownEntries = computed(() => {
  if (!search.value) {
    return props.attributes;
  }
  return props.attributes.filter((attr) =>
    getAttributeName(attr).toLowerCase().includes(search.value.toLowerCase())
  );
});

const areAllOptionsSelected = computed({
  get() {
    return (
      !!dropdownEntries.value.length &&
      dropdownEntries.value.every((option) =>
        selected.value.includes(getAttributeId(option))
      )
    );
  },
  set(value) {
    selected.value = value ? dropdownEntries.value.map(getAttributeId) : [];
  },
});

const getAttributeName = (attribute: IStepAttribute) =>
  attributeOptions.value.find(
    (option) => getAttributeId(attribute) === getAttributeId(option)
  )?.name || wordsFirstLetterToUpper(attribute.id, "_");

const handleSave = () => {
  emit(
    "save",
    props.attributes.filter((attr) =>
      selected.value.includes(getAttributeId(attr))
    )
  );
};
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.dropdown {
  min-width: 300px;
  width: auto;
}

.list-item {
  @apply flex items-center space-x-2 px-1 py-0-5;

  & input[type="checkbox"] {
    @apply rounded-xs border border-gray-300;
  }

  & label {
    @apply cursor-pointer text-sm font-normal;
  }
}
</style>
